import React, { useState, useEffect, memo } from "react";
import { fetchStreamedChatCompletion } from "./Chat"; 
import { motion, AnimatePresence } from "framer-motion";
import BlurText from "../components/BlurText"; 
import { Sparkle, CaretUp, CaretDown } from "@phosphor-icons/react";

const WatchlistSummary = memo(({ watchlist, isActive, subjectArea }) => {
  const [summary, setSummary] = useState("");
  const [headline, setHeadline] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true); // ✅ Track if the summary is expanded

  useEffect(() => {
    if (!hasGenerated) {
      generateSummary();
    }
  }, [isActive]);

  const generateSummary = async () => {
    setIsLoading(true);
    setHasAnimated(false);
    setHasGenerated(true);

    const systemMessage = {
      role: "system",
      content: `You are a Jason Zweig's finance assistant. Generate a very short, engaging headline (max 5 words) and a concise summary (max 30 words) of this ${subjectArea != null ? subjectArea:"watchlist"}'s performance.

      - Make it in the style of writing like Jason Zweig from the Wall Street Journal.
      - Keep the headline catchy but informative.
      - The summary should highlight trends, major stock movements, and sentiment.
      - The sentiment of the headline should match the ${subjectArea != null ? subjectArea:"watchlist"} data, especially the percentage change of the day.

      Make it super super Jason Zweig and super Wall Street Journal, that's SUPER IMPORTANT
      LIKE ULTRA WSJ I want it oozing with WSJ energy, everything WSJ!!!!
      Now give me kinda sassy WSJ energy,

      Here is the ${subjectArea != null ? subjectArea:"watchlist"} data:
      ${JSON.stringify(watchlist)}

      Output format:
      Headline: Headline goes here (keep it sentence case)
      Summary: Something about the ${subjectArea != null ? subjectArea:"watchlist"} stocks
      `,
    };

    try {
      let assistantResponse = "";
      const streamData = await fetchStreamedChatCompletion([systemMessage]);

      for await (const chunk of streamData) {
        if (chunk === "[DONE]") break;
        assistantResponse += chunk;
      }

      const responseParts = assistantResponse.split("\n");
      const extractedHeadline = responseParts[0]?.replace("Headline: ", "").trim() || "Market Update";
      const extractedSummary = responseParts.slice(1).join(" ").replace("Summary: ", "").trim();
      setHeadline(extractedHeadline);
      setSummary(extractedSummary);
      setTimeout(() => setHasAnimated(true), 2000); // ✅ Slight delay before animating in
    } catch (error) {
      console.error("Error generating AI summary:", error);
      setHeadline("Market Analysis");
      setSummary("Unable to generate summary at this time.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: -10 }} 
      animate={{ opacity: 1, y: 0 }} 
      transition={{ duration: 0.5 }}
      className={isLoading ? "ai-summary-card loading" : "ai-summary-card"}
      style={{
        padding: "16px",
        marginBottom: "12px",
      }}
    >
      {/* ✅ AI Status Bar */}
      <div className="ai-status">
      <div className={isLoading ? "ai-flashline loading" : "ai-flashline"}>
        <Sparkle weight="fill" size={12} />
        <p>{isLoading ? "AI GENERATING" : "AI GENERATED"}</p>
      </div>

      {/* ✅ Toggle Button for Collapsing */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          alignItems: "center",
          justifyContent: "space-between",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          fontSize: "16px",
          fontWeight: "bold",
          // marginBottom: "8px",
          padding: "0",
          color: "#6F6F6F",
        }}
      >        {isExpanded ? <CaretUp size={24} /> : <CaretDown size={24} />}
      </button>
      </div>
                   
                      {/* ✅ Animated Headline */}
        <h3 style={{ fontSize: "18px", fontWeight: "bold", marginBottom: "0px" }}>
                  <BlurText
                    text={headline}
                    delay={15}
                    animateBy="words"
                    direction="top"
                    className="text-xl font-bold"
                  />
                </h3>
      {/* ✅ Collapsible Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            style={{ overflow: "hidden" }}
            className="ai-content"
          >
            {/* ✅ Loading Text */}
            {isLoading ? (
              <p style={{ fontSize: "14px", fontStyle: "italic" }}>Analyzing your watchlist...</p>
            ) : (
              <>


                {/* ✅ Animated Summary */}
                <div className="summary">
                  <BlurText
                    text={summary}
                    delay={25}
                    animateBy="words"
                    direction="top"
                    className="text-md"
                  />
                </div>
              </>
            )}
      {/* <button
        onClick={generateSummary}
        disabled={isLoading}
        className="generate-again-button"
      >
        Generate again
      </button> */}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
});

export default WatchlistSummary;
